import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import ProductDetailModal from './ProductDetailModal'
import '../../styles/pages.css'

/**
 * Live KG image identification — the MBR demo tool. Upload one or more photos
 * (hero first), optionally with title/brand/category text, and the SigLIP
 * matcher returns the top-k product-model candidates. Text narrows the
 * shortlist (the strong mode); photos alone run open-set over the catalogue
 * (weaker, unmeasured). Backs POST /api/graph/identify — 503 until the vision
 * sidecar is wired up.
 *
 * Presentation rule: the confidence signal is the MARGIN over the runner-up,
 * read against the per-category accept threshold — never the raw similarity.
 * SigLIP is sigmoid-trained, so correct matches score ~0.10–0.25 and wrong
 * ones sit just below; the absolute number looks "low" by design.
 */
function CatalogIdentify() {
  const [files, setFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [title, setTitle] = useState('')
  const [brand, setBrand] = useState('')
  const [category, setCategory] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  // Object URLs for the local thumbnails; revoke on change to avoid leaks.
  useEffect(() => {
    const urls = files.map(f => URL.createObjectURL(f))
    setPreviews(urls)
    return () => urls.forEach(u => URL.revokeObjectURL(u))
  }, [files])

  function handleFiles(event) {
    setFiles(Array.from(event.target.files || []))
    setResult(null)
    setError(null)
  }

  async function handleMatch() {
    if (files.length === 0) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const form = new FormData()
      files.forEach(file => form.append('photos', file))
      if (title.trim()) form.append('title', title.trim())
      if (brand.trim()) form.append('brand', brand.trim())
      if (category.trim()) form.append('category', category.trim())
      const response = await api.postForm('/api/graph/identify', form)
      setResult(response)
    } catch (err) {
      setError(err.status === 503
        ? 'Image identification is not wired up yet — the vision service is still being deployed.'
        : err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-4)', maxWidth: 640 }}>
        Upload a photo of an item and the knowledge graph guesses which product model it is.
        Add a title, brand or category to narrow the search — that is the strong mode.
        A photo on its own searches the whole catalogue.
      </div>

      <div className="card" style={{ padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 640 }}>
          <div>
            <div className="filter-label">Photos (hero first)</div>
            <input type="file" accept="image/*" multiple onChange={handleFiles} className="form-input" />
          </div>
          {previews.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {previews.map((url, i) => (
                <img key={url} src={url} alt={`upload ${i + 1}`}
                  style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4, background: 'var(--color-bg-tertiary)' }} />
              ))}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-2)' }}>
            <div>
              <div className="filter-label">Title (optional)</div>
              <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Player Strat" />
            </div>
            <div>
              <div className="filter-label">Brand (optional)</div>
              <input className="form-input" value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. Fender" />
            </div>
            <div>
              <div className="filter-label">Category (optional)</div>
              <input className="form-input" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Electric Guitars" />
            </div>
          </div>
          <div>
            <button type="button" className="btn btn-primary" onClick={handleMatch} disabled={loading || files.length === 0}>
              {loading ? 'Matching…' : 'Match'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {result && <IdentifyResult result={result} />}
    </div>
  )
}

/** The verdict: a status line + the ranked candidate cards. A click on a card
 * opens the same product-detail modal the review page uses. */
function IdentifyResult({ result }) {
  const [openId, setOpenId] = useState(null)

  if (!result.candidates || result.candidates.length === 0) {
    return <div className="empty-state">No candidate models matched.</div>
  }
  return (
    <div>
      <div className="text-sm text-muted" style={{ marginBottom: 'var(--space-3)' }}>
        Verdict: <span className={statusBadge(result.status)}>{result.status}</span>
        {result.open_set
          ? ' · searched the whole catalogue (no text filter)'
          : ` · narrowed to ${result.category_used || 'category'}${result.threshold != null ? ` (auto-accept margin ≥ ${result.threshold})` : ''}`}
        {result.status === 'candidate' && result.threshold != null && ' · auto-accept is switched off (assist mode)'}
      </div>
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        {result.candidates.map((c, index) => (
          <CandidateCard
            key={c.product_model_id}
            candidate={c}
            rank={index + 1}
            threshold={result.threshold}
            onOpen={() => setOpenId(c.product_model_id)}
          />
        ))}
      </div>
      <div className="text-sm text-muted" style={{ marginTop: 'var(--space-3)', maxWidth: 640 }}>
        Confidence is the margin over the runner-up, read against the category's accept
        threshold. Similarity is the raw SigLIP score: correct matches typically land
        between 0.10 and 0.25, so a small number is normal.
      </div>
      {openId && (
        <ProductDetailModal
          productModelId={openId}
          onClose={() => setOpenId(null)}
          onMutated={() => {}}
        />
      )}
    </div>
  )
}

// Bar scale when no calibrated threshold applies (open-set / brand-only):
// twice the pedal threshold, so a strong margin still reads as full.
const FALLBACK_BAR_SCALE = 0.042

/** One ranked candidate, with a reference thumbnail pulled from the bank. */
function CandidateCard({ candidate, rank, threshold, onOpen }) {
  const [thumb, setThumb] = useState(null)
  const margin = candidate.margin ?? 0
  const scale = threshold != null ? threshold * 2 : FALLBACK_BAR_SCALE
  const fill = Math.max(0, Math.min(1, margin / scale))
  const clears = threshold != null && rank === 1 && margin >= threshold

  useEffect(() => {
    let cancelled = false
    api.get(`/api/graph/review/products/${candidate.product_model_id}/reference-images`)
      .then(res => { if (!cancelled) setThumb((res.images || [])[0]?.signed_url || null) })
      .catch(() => { if (!cancelled) setThumb(null) })
    return () => { cancelled = true }
  }, [candidate.product_model_id])

  return (
    <div
      className="card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      title="Open product details"
      style={{ display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-3)', alignItems: 'center', cursor: 'pointer' }}
    >
      <div style={{ fontWeight: 600, width: 24, textAlign: 'center' }}>{rank}</div>
      {thumb
        ? <img src={thumb} alt={candidate.display_name} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, background: 'var(--color-bg-tertiary)' }} />
        : <div style={{ width: 64, height: 64, borderRadius: 4, background: 'var(--color-bg-tertiary)' }} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600 }}>{candidate.display_name}</div>
        <div className="text-sm text-muted">
          {[candidate.brand, candidate.category].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 180 }}>
        <div className="text-sm" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', alignItems: 'center' }}>
          <span>margin {margin.toFixed(3)}</span>
          {clears && <span className="badge badge-success">would auto-accept</span>}
        </div>
        <div
          aria-hidden="true"
          style={{ position: 'relative', height: 6, marginTop: 4, borderRadius: 3, background: 'var(--color-bg-tertiary)', overflow: 'hidden' }}
        >
          <div style={{ width: `${fill * 100}%`, height: '100%', background: clears ? 'var(--color-success)' : 'var(--color-primary)' }} />
          {threshold != null && (
            <div style={{ position: 'absolute', left: '50%', top: 0, width: 1, height: '100%', background: 'var(--color-text-muted)' }} />
          )}
        </div>
        <div className="text-sm text-muted" style={{ marginTop: 4 }}>similarity {candidate.score?.toFixed(3)}</div>
      </div>
    </div>
  )
}

function statusBadge(status) {
  if (status === 'resolved') return 'badge badge-success'
  if (status === 'candidate') return 'badge badge-warning'
  return 'badge badge-neutral'
}

export default CatalogIdentify
